import { DownOutlined } from '@ant-design/icons';
import { useState } from 'react';
import cs from 'classnames';
import Styles from '../../../Styles.module.scss';
import { getBrandDomain } from '../../../../../common/utils/getBrandDomain';

export default function Serve() {
    const brandDomain = getBrandDomain();
    const [expanded, setExpanded] = useState(false);

    return (
        <div className={Styles.CommandCard}>
            <div className={Styles.CommandHeader} onClick={() => setExpanded(!expanded)}>
                <div>
                    <span className={Styles.CommandName}>modelpark serve</span>
                    <span className={Styles.CommandSummary}>Tunnel an already-running local port</span>
                </div>
                <DownOutlined className={cs(Styles.CommandExpandIcon, { [Styles.expanded]: expanded })} />
            </div>

            {expanded && (
                <div className={Styles.CommandBody}>
                    <p className={Styles.CommandDescription}>
                        Expose a local port that is already running to the internet via a secure tunnel.
                        For REST APIs, add <code>--type api</code> to enable API key authentication.
                        Your application will be accessible at <code>https://{'<name>'}.{brandDomain}</code>.
                        Runs in the background by default; use <code>--session</code> to keep it in the foreground.
                        If your app isn't running yet, use <code>modelpark run</code> instead to start and tunnel in one step.
                    </p>

                    <div className={Styles.CommandExample}>
                        <div className={Styles.CommandExampleLabel}>Options</div>
                        <table className={Styles.OptionsTable}>
                            <thead>
                                <tr>
                                    <th>Flag</th>
                                    <th>Short</th>
                                    <th>Default</th>
                                    <th>Description</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td>
                                        --name
                                        <span className={Styles.Required}>*required</span>
                                    </td>
                                    <td>-n</td>
                                    <td>-</td>
                                    <td>App name / subdomain</td>
                                </tr>
                                <tr>
                                    <td>
                                        --port
                                        <span className={Styles.Required}>*required</span>
                                    </td>
                                    <td>-p</td>
                                    <td>8000</td>
                                    <td>Local port to expose</td>
                                </tr>
                                <tr>
                                    <td>--access</td>
                                    <td>-a</td>
                                    <td>private</td>
                                    <td>Access type (public, private, protected)</td>
                                </tr>
                                <tr>
                                    <td>--framework</td>
                                    <td>-f</td>
                                    <td>-</td>
                                    <td>Framework hint (streamlit, gradio, etc.)</td>
                                </tr>
                                <tr>
                                    <td>--type</td>
                                    <td>-</td>
                                    <td>auto-detect</td>
                                    <td>App type: api, web</td>
                                </tr>
                                <tr>
                                    <td>--token</td>
                                    <td>-t</td>
                                    <td>-</td>
                                    <td>Authentication token (optional if logged in)</td>
                                </tr>
                                <tr>
                                    <td>--session</td>
                                    <td>-s</td>
                                    <td>false</td>
                                    <td>Run in foreground (session mode)</td>
                                </tr>
                                <tr>
                                    <td>--server</td>
                                    <td>-</td>
                                    <td>{`wss://tunnel.${brandDomain}/ws`}</td>
                                    <td>Tunnel server WebSocket URL</td>
                                </tr>
                                <tr>
                                    <td>--api-server</td>
                                    <td>-</td>
                                    <td>{`https://${brandDomain}`}</td>
                                    <td>API server URL</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    <div className={Styles.CommandExample}>
                        <div className={Styles.CommandExampleLabel}>Examples</div>
                        <div className={Styles.CodeBlock} style={{ marginBottom: 8 }}>
                            <code># Basic usage (runs in background)</code>
                            <br />
                            <code>modelpark serve --name myapp --port 8000</code>
                        </div>
                        <div className={Styles.CodeBlock} style={{ marginBottom: 8 }}>
                            <code># Keep in foreground (session mode)</code>
                            <br />
                            <code>modelpark serve -n myapp -p 8501 --session</code>
                        </div>
                        <div className={Styles.CodeBlock} style={{ marginBottom: 8 }}>
                            <code># Streamlit with public access and framework hint</code>
                            <br />
                            <code>modelpark serve -n myapp -p 8501 --access public --framework streamlit</code>
                        </div>
                        <div className={Styles.CodeBlock} style={{ marginBottom: 8 }}>
                            <code># With short flags</code>
                            <br />
                            <code>modelpark serve -n demo -p 3000</code>
                        </div>
                        <div className={Styles.CodeBlock} style={{ marginBottom: 8 }}>
                            <code># REST API (API key auth enabled)</code>
                            <br />
                            <code>modelpark serve --name myapi --port 8000 --type api</code>
                        </div>
                        <div className={Styles.CodeBlock}>
                            <code># Public REST API (no auth required)</code>
                            <br />
                            <code>modelpark serve --name myapi --port 8000 --type api --access public</code>
                        </div>
                    </div>

                    <div className={Styles.CommandExample}>
                        <div className={Styles.CommandExampleLabel}>Output (background mode)</div>
                        <div className={Styles.CodeBlock}>
                            <code>{`Registering app 'myapp'...
App 'myapp' registered (public, streamlit)
Started 'myapp' (pid 12345)
  Local:  http://localhost:8000
  Remote: https://myapp.${brandDomain}
  Logs:   ~/.modelpark/logs/myapp.log`}</code>
                        </div>
                    </div>

                    <div className={Styles.CommandExample}>
                        <div className={Styles.CommandExampleLabel}>Output (session mode with --session)</div>
                        <div className={Styles.CodeBlock}>
                            <code>{`🚀 ModelPark Tunnel
   Local:  http://localhost:8000
   Remote: https://myapp.${brandDomain}`}</code>
                        </div>
                    </div>

                    <div className={Styles.CommandExample}>
                        <div className={Styles.CommandExampleLabel}>Common Use Cases</div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                            <div className={Styles.CodeBlock}>
                                <code># Gradio app (default port 7860)</code>
                                <br />
                                <code>modelpark serve -n my-gradio-app -p 7860</code>
                            </div>
                            <div className={Styles.CodeBlock}>
                                <code># Streamlit app (default port 8501)</code>
                                <br />
                                <code>modelpark serve -n my-streamlit-app -p 8501</code>
                            </div>
                            <div className={Styles.CodeBlock}>
                                <code># FastAPI app</code>
                                <br />
                                <code>modelpark serve -n my-api -p 8000</code>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
